import React, { useState, useEffect } from 'react';
// import { useAuth } from '../contexts/AuthContext';
import { User, Mail, Calendar, TrendingUp, Music, Edit, Save, X } from 'lucide-react';
import api from '../services/api';

const Profile = () => {
  // const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editEmail, setEditEmail] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchProfile();
    fetchStats();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/api/users/profile');
      console.log('Profile data:', response.data.user);
      setProfile(response.data.user);
      setEditEmail(response.data.user.email);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/api/users/stats');
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/api/users/profile', { email: editEmail });
      setProfile(prev => ({ ...prev, email: editEmail }));
      setEditing(false);
    } catch (error) {
      console.error('Failed to update profile:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditEmail(profile.email);
    setEditing(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold-50"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <div className="relative bg-black/90 rounded-lg p-6 shadow-[0_4px_4px_rgba(0,0,0,0.17)] overflow-hidden group">
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/[0.095] to-transparent opacity-0 group-hover:opacity-100 transition-all duration-700 transform -translate-x-full group-hover:translate-x-full"></div>
        <div className="flex items-center justify-between relative z-10">
          <div className="flex items-center space-x-4">
            <div>
              <h1 className="text-2xl font-bold text-white">Profile</h1>
              <p className="text-gray-400">Manage your account settings</p>
            </div>
          </div>
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-300 bg-black/50 border border-white/20 rounded-md hover:bg-black/70 focus:outline-none focus:ring-2 focus:ring-gold-50 focus:border-gold-50 transition-colors backdrop-blur-sm"
            >
              <Edit className="h-4 w-4" />
              <span>Edit</span>
            </button>
          )}
        </div>
      </div>

      {/* Profile Information */}
      <div className="relative bg-black/90 rounded-lg shadow-[0_4px_4px_rgba(0,0,0,0.17)] p-6 overflow-hidden group">
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/[0.095] to-transparent opacity-0 group-hover:opacity-100 transition-all duration-700 transform -translate-x-full group-hover:translate-x-full"></div>
        <h2 className="text-lg font-semibold text-white mb-6 relative z-10">Account Information</h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 relative z-10">
          {/* Left Column - Profile Image */}
          <div className="flex flex-col items-center space-y-3">
            <div className="relative">
              {profile?.image ? (
                <>
                  <img 
                    src={`https://mediablox-submission-pipeline.s3.amazonaws.com/images/users/${profile.image}`}
                    alt="Profile"
                    className="h-36 w-36 rounded-full object-cover border-2 border-white/20"
                    onError={(e) => {
                      console.error('Failed to load profile image:', e.target.src);
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="h-36 w-36 rounded-full bg-gray-700 flex items-center justify-center border-2 border-white/20 absolute inset-0" style={{ display: 'none' }}>
                    <User className="h-6 w-6 text-gray-400" />
                  </div>
                </>
              ) : (
                <div className="h-36 w-36 rounded-full bg-gray-700 flex items-center justify-center border-2 border-white/20">
                  <User className="h-6 w-6 text-gray-400" />
                </div>
              )}
            </div>
            <label className="text-sm font-medium text-gray-300">
              Profile Image
            </label>
          </div>

          {/* Right Column - Profile Details */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Email Address
              </label>
              {editing ? (
                <div className="flex items-center space-x-2">
                  <input
                    type="email"
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                    className="flex-1 px-3 py-2 border border-white/20 rounded-md focus:outline-none focus:ring-2 focus:ring-gold-50 focus:border-gold-50 bg-black/50 text-white backdrop-blur-sm"
                  />
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-3 py-2 bg-gold-50 text-white rounded-md hover:bg-gold-100 focus:outline-none focus:ring-2 focus:ring-gold-50 disabled:opacity-50 transition-colors hover:text-black"
                  >
                    <Save className="h-4 w-4" />
                  </button>
                  <button
                    onClick={handleCancel}
                    className="px-3 py-2 bg-gray-600 text-gray-200 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <Mail className="h-5 w-5 text-gray-400" />
                  <span className="text-white">{profile?.email}</span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                User ID
              </label>
              <div className="flex items-center space-x-2">
                <User className="h-5 w-5 text-gray-400" />
                <span className="text-white font-mono text-sm">{profile?.id}</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Member Since
              </label>
              <div className="flex items-center space-x-2">
                <Calendar className="h-5 w-5 text-gray-400" />
                <span className="text-white">
                  {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics */}
      {stats && (
        <div className="relative bg-black/90 rounded-lg shadow-[0_4px_4px_rgba(0,0,0,0.17)] p-6 overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/[0.095] to-transparent opacity-0 group-hover:opacity-100 transition-all duration-700 transform -translate-x-full group-hover:translate-x-full"></div>
          <h2 className="text-lg font-semibold text-white mb-4 relative z-10">Submission Statistics</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 relative z-10">
            <div className="relative bg-black/60 rounded-lg p-4 border border-blue-500/30 overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-transparent via-blue-500/[0.05] to-transparent opacity-0 group-hover:opacity-100 transition-all duration-700 transform -translate-x-full group-hover:translate-x-full"></div>
              <div className="flex items-center space-x-3 relative z-10">
                <TrendingUp className="h-8 w-8 text-blue-400" />
                <div>
                  <p className="text-sm font-medium text-blue-400">Total Submissions</p>
                  <p className="text-2xl font-bold text-white">{stats.total_submissions || 0}</p>
                </div>
              </div>
            </div>

            <div className="relative bg-black/60 rounded-lg p-4 border border-yellow-500/30 overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-transparent via-yellow-500/[0.05] to-transparent opacity-0 group-hover:opacity-100 transition-all duration-700 transform -translate-x-full group-hover:translate-x-full"></div>
              <div className="flex items-center space-x-3 relative z-10">
                <Calendar className="h-8 w-8 text-yellow-400" />
                <div>
                  <p className="text-sm font-medium text-yellow-400">Pending</p>
                  <p className="text-2xl font-bold text-white">{stats.pending_submissions || 0}</p>
                </div>
              </div>
            </div>

            <div className="relative bg-black/60 rounded-lg p-4 border border-green-500/30 overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-transparent via-green-500/[0.05] to-transparent opacity-0 group-hover:opacity-100 transition-all duration-700 transform -translate-x-full group-hover:translate-x-full"></div>
              <div className="flex items-center space-x-3 relative z-10">
                <Music className="h-8 w-8 text-green-400" />
                <div>
                  <p className="text-sm font-medium text-green-400">Approved</p>
                  <p className="text-2xl font-bold text-white">{stats.approved_submissions || 0}</p>
                </div>
              </div>
            </div>

            <div className="relative bg-black/60 rounded-lg p-4 border border-red-500/30 overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-transparent via-red-500/[0.05] to-transparent opacity-0 group-hover:opacity-100 transition-all duration-700 transform -translate-x-full group-hover:translate-x-full"></div>
              <div className="flex items-center space-x-3 relative z-10">
                <X className="h-8 w-8 text-red-400" />
                <div>
                  <p className="text-sm font-medium text-red-400">Rejected</p>
                  <p className="text-2xl font-bold text-white">{stats.rejected_submissions || 0}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* API Information */}
      <div className="relative bg-black/90 rounded-lg shadow-[0_4px_4px_rgba(0,0,0,0.17)] p-6 overflow-hidden group">
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/[0.095] to-transparent opacity-0 group-hover:opacity-100 transition-all duration-700 transform -translate-x-full group-hover:translate-x-full"></div>
        <h2 className="text-lg font-semibold text-white mb-4 relative z-10">API Access</h2>
        
        <div className="bg-black/50 rounded-lg p-4 relative z-10">
          <p className="text-sm text-gray-400 mb-2">
            Use your API token to access the submission pipeline programmatically:
          </p>
          <div className="bg-black rounded p-3 border border-white/20">
            <code className="text-green-400 text-sm">
              {`curl -X POST http://localhost:3001/api/submissions/api
              -H "Authorization: Bearer YOUR_TOKEN"
              -H "Content-Type: application/json"
              -d '{"title": "My Track", "audio_file_base64": "...", "audio_file_name": "track.wav"}'`}
            </code>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile; 